/**
 * Notas de Crédito de VENTA (emisor CHIRINO) — Iter 236.s Feb 2026, Carlos.
 *
 * Tab paralelo al de Compra. Aplica NCs contra facturas de venta CHIRINO:
 *   - Crea (o vincula) credit_note en Alegra sobre la factura
 *   - Rebaja saldo, anula proforma origen, crea proforma nueva en borrador
 */
import { useEffect, useMemo, useState, useCallback } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  RefreshCw,
  Link2,
  Unlink,
  CheckCircle2,
  AlertTriangle,
  FileMinus,
  Search,
  Loader2,
  Zap,
  ArrowRight,
} from 'lucide-react';
import { toast } from 'sonner';
import { notasCreditoVentaApi } from '../../lib/api';
import { TableSkeleton } from '../../lib/skeletons';
import { fmtFecha as fmtDate } from '../../lib/utils';

const fmtCLP = (n) => `$${(Number(n) || 0).toLocaleString('es-CL')}`;

const CATEGORIA_META = {
  sin_link: {
    label: 'Sin linkear',
    cls: 'bg-amber-100 text-amber-800 border-amber-300',
    desc: 'NC sin referencia al folio original — requiere linkeo manual',
    icon: '⚠️',
  },
  link_xml: {
    label: 'Linkeada (XML)',
    cls: 'bg-blue-100 text-blue-800 border-blue-300',
    desc: 'Auto-linkeada desde la referencia del XML del SII',
    icon: '🔗',
  },
  link_manual: {
    label: 'Linkeada (manual)',
    cls: 'bg-indigo-100 text-indigo-800 border-indigo-300',
    desc: 'Linkeada manualmente por un usuario',
    icon: '✋',
  },
  aplicada: {
    label: 'Aplicada',
    cls: 'bg-green-100 text-green-800 border-green-300',
    desc: 'NC aplicada — proforma anulada y proforma recreada en borrador',
    icon: '✓',
  },
  sin_factura_original: {
    label: 'Factura original no existe',
    cls: 'bg-red-100 text-red-800 border-red-300',
    desc: 'XML referencia un folio que NO está en el ERP',
    icon: '🚫',
  },
};

export default function NotasCreditoVentaTab() {
  const [subtab, setSubtab] = useState('sin_link');
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ items: [], total: 0, kpis: {} });
  const [clienteFiltro, setClienteFiltro] = useState('');
  const [refreshSpin, setRefreshSpin] = useState(false);

  // Dialog: linkear manual
  const [linkDlg, setLinkDlg] = useState(null);
  const [linkSearchTerm, setLinkSearchTerm] = useState('');
  const [linkLoading, setLinkLoading] = useState(false);
  const [submittingLink, setSubmittingLink] = useState(false);

  // Dialog: aplicar (dry-run + apply)
  const [aplicarDlg, setAplicarDlg] = useState(null);
  const [motivo, setMotivo] = useState('');
  const [preview, setPreview] = useState(null);
  const [previewBusy, setPreviewBusy] = useState(false);
  const [applyBusy, setApplyBusy] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const params = { days_back: 730 };
      if (clienteFiltro.trim()) params.cliente = clienteFiltro.trim();
      const r = await notasCreditoVentaApi.list(params);
      setData(r.data || { items: [], total: 0, kpis: {} });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error cargando NCs de venta');
    } finally {
      setLoading(false);
    }
  }, [clienteFiltro]);

  useEffect(() => {
    reload();
  }, [reload]);

  const filteredItems = useMemo(
    () => (data.items || []).filter((it) => it.categoria === subtab),
    [data.items, subtab],
  );

  const kpis = data.kpis || {};

  const handleAutoLink = async () => {
    setRefreshSpin(true);
    try {
      const r = await notasCreditoVentaApi.autoLinkearBatch();
      const d = r.data || {};
      toast.success(
        `Auto-link: ${d.linked_count} linkeadas, ${d.skipped_no_match} sin factura original`,
      );
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error en auto-link batch');
    } finally {
      setRefreshSpin(false);
    }
  };

  const openLinkDialog = async (nc) => {
    setLinkDlg({ nc, sugerencias: [] });
    setLinkSearchTerm('');
    setLinkLoading(true);
    try {
      const r = await notasCreditoVentaApi.sugerencias(nc.folio);
      setLinkDlg({ nc, sugerencias: r.data?.sugerencias || [] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error cargando sugerencias');
    } finally {
      setLinkLoading(false);
    }
  };

  const submitLink = async (factura_folio) => {
    if (!factura_folio || !linkDlg?.nc) return;
    setSubmittingLink(true);
    try {
      await notasCreditoVentaApi.linkear(linkDlg.nc.folio, factura_folio);
      toast.success(`NC ${linkDlg.nc.folio} linkeada a factura ${factura_folio}`);
      setLinkDlg(null);
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error linkeando');
    } finally {
      setSubmittingLink(false);
    }
  };

  const quitarLink = async (folio) => {
    if (!window.confirm(`¿Quitar el link manual de la NC ${folio}?`)) return;
    try {
      await notasCreditoVentaApi.quitarLink(folio);
      toast.success(`Link quitado de NC ${folio}`);
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error quitando link');
    }
  };

  const openAplicarDialog = (nc) => {
    setMotivo('');
    setPreview(null);
    setAplicarDlg({ nc });
  };

  const previsualizar = async () => {
    if (!aplicarDlg?.nc) return;
    setPreviewBusy(true);
    try {
      const r = await notasCreditoVentaApi.aplicar(aplicarDlg.nc.folio, {
        motivo: motivo.trim(),
        dry_run: true,
      });
      setPreview(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al previsualizar');
      setPreview(null);
    } finally {
      setPreviewBusy(false);
    }
  };

  const aplicarReal = async () => {
    if (!aplicarDlg?.nc) return;
    if (motivo.trim().length < 5) {
      toast.error('El motivo debe tener al menos 5 caracteres');
      return;
    }
    setApplyBusy(true);
    try {
      const r = await notasCreditoVentaApi.aplicar(aplicarDlg.nc.folio, {
        motivo: motivo.trim(),
        dry_run: false,
      });
      toast.success(r.data?.mensaje || `NC ${aplicarDlg.nc.folio} aplicada`);
      setAplicarDlg(null);
      setPreview(null);
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al aplicar');
    } finally {
      setApplyBusy(false);
    }
  };

  const sugerenciasFiltradas = useMemo(() => {
    if (!linkDlg?.sugerencias) return [];
    const q = linkSearchTerm.trim().toLowerCase();
    if (!q) return linkDlg.sugerencias;
    return linkDlg.sugerencias.filter(
      (s) =>
        String(s.folio || '').toLowerCase().includes(q) ||
        String(s.total || '').includes(q) ||
        String(s.fecha_emision || '').includes(q),
    );
  }, [linkDlg, linkSearchTerm]);

  return (
    <div className="space-y-4" data-testid="notas-credito-venta-tab">
      {/* Header controles */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <p className="text-sm text-gray-600 max-w-2xl">
          NCs emitidas por CHIRINO (DTE 61, emisor CHIRINO STEEL). Al aplicar, se
          rebaja la factura de venta asociada, se anula la proforma origen y se crea
          una <b>nueva proforma en borrador</b> con la misma información para su
          regularización o anulación definitiva.
        </p>
        <div className="flex items-center gap-2">
          <Input
            placeholder="Filtrar cliente (nombre/RUT)…"
            value={clienteFiltro}
            onChange={(e) => setClienteFiltro(e.target.value)}
            className="w-64"
            data-testid="ncv-cliente-filter"
          />
          <Button
            onClick={reload}
            variant="outline"
            disabled={loading}
            data-testid="ncv-refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Recargar
          </Button>
          <Button
            onClick={handleAutoLink}
            variant="default"
            disabled={refreshSpin}
            data-testid="ncv-auto-link-batch-btn"
            title="Auto-linkea NCs cuyo XML referencia una factura CHIRINO existente"
          >
            <Zap className={`w-4 h-4 mr-2 ${refreshSpin ? 'animate-pulse' : ''}`} />
            Auto-link batch
          </Button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <KpiCard label="Total NCs" value={kpis.total || 0} tone="gray" />
        <KpiCard
          label="Sin linkear"
          value={kpis.sin_link || 0}
          tone="amber"
          testid="ncv-kpi-sin-link"
        />
        <KpiCard label="Link XML" value={kpis.link_xml || 0} tone="blue" />
        <KpiCard label="Link manual" value={kpis.link_manual || 0} tone="indigo" />
        <KpiCard label="Aplicadas" value={kpis.aplicada || 0} tone="green" />
        <KpiCard
          label="Monto pendiente"
          value={fmtCLP(kpis.monto_pendiente_total || 0)}
          tone="red"
          numeric={false}
          testid="ncv-kpi-monto-pendiente"
        />
      </div>

      {/* Subtabs */}
      <Tabs value={subtab} onValueChange={setSubtab}>
        <TabsList className="grid grid-cols-5 w-full max-w-3xl">
          <TabsTrigger value="sin_link" data-testid="ncv-tab-sin-link">
            Sin link ({kpis.sin_link || 0})
          </TabsTrigger>
          <TabsTrigger value="link_xml" data-testid="ncv-tab-link-xml">
            Auto ({kpis.link_xml || 0})
          </TabsTrigger>
          <TabsTrigger value="link_manual" data-testid="ncv-tab-link-manual">
            Manual ({kpis.link_manual || 0})
          </TabsTrigger>
          <TabsTrigger value="aplicada" data-testid="ncv-tab-aplicada">
            Aplicadas ({kpis.aplicada || 0})
          </TabsTrigger>
          <TabsTrigger value="sin_factura_original" data-testid="ncv-tab-sin-factura">
            Sin fact. orig. ({kpis.sin_factura_original || 0})
          </TabsTrigger>
        </TabsList>

        {['sin_link', 'link_xml', 'link_manual', 'aplicada', 'sin_factura_original'].map(
          (cat) => (
            <TabsContent key={cat} value={cat} className="mt-4">
              <CategoryHelp cat={cat} />
              {loading ? (
                <TableSkeleton rows={8} cols={7} />
              ) : (
                <NCVentaTable
                  items={filteredItems}
                  onLink={openLinkDialog}
                  onQuitarLink={quitarLink}
                  onAplicar={openAplicarDialog}
                />
              )}
            </TabsContent>
          ),
        )}
      </Tabs>

      {/* Dialog: Linkear */}
      <Dialog open={!!linkDlg} onOpenChange={(o) => !o && setLinkDlg(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Linkear NC de venta a factura original</DialogTitle>
            <DialogDescription>
              {linkDlg && (
                <span>
                  NC <b>{linkDlg.nc.folio}</b> a{' '}
                  <b>{linkDlg.nc.receptor_razon_social}</b> por{' '}
                  <b>{fmtCLP(linkDlg.nc.total)}</b> · {fmtDate(linkDlg.nc.fecha_emision)}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="flex items-center gap-2 mb-2">
            <Search className="w-4 h-4 text-gray-400" />
            <Input
              placeholder="Buscar folio / monto / fecha…"
              value={linkSearchTerm}
              onChange={(e) => setLinkSearchTerm(e.target.value)}
              data-testid="ncv-link-search"
            />
          </div>

          {linkLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
              <span className="ml-2 text-gray-500">Cargando sugerencias…</span>
            </div>
          ) : sugerenciasFiltradas.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No hay facturas de venta del mismo cliente en los últimos 365 días.
              <p className="text-xs mt-2">
                Puedes ingresar el folio manualmente abajo si lo conoces.
              </p>
            </div>
          ) : (
            <div className="border rounded max-h-96 overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="text-left px-2 py-1">Folio</th>
                    <th className="text-left px-2 py-1">Fecha</th>
                    <th className="text-right px-2 py-1">Total</th>
                    <th className="text-right px-2 py-1">Δ NC</th>
                    <th className="text-left px-2 py-1">OT</th>
                    <th className="text-center px-2 py-1">Match</th>
                    <th className="text-center px-2 py-1">Acción</th>
                  </tr>
                </thead>
                <tbody>
                  {sugerenciasFiltradas.map((s) => (
                    <tr key={s.folio} className="border-t hover:bg-blue-50">
                      <td className="px-2 py-1 font-mono">{s.folio}</td>
                      <td className="px-2 py-1 text-gray-600">
                        {fmtDate(s.fecha_emision)}
                      </td>
                      <td className="px-2 py-1 text-right">{fmtCLP(s.total)}</td>
                      <td className="px-2 py-1 text-right text-gray-500">
                        {fmtCLP(s.diff_monto)}
                      </td>
                      <td className="px-2 py-1 text-xs text-gray-500">
                        {s.numero_ot || '—'}
                      </td>
                      <td className="px-2 py-1 text-center">
                        {s.match_exacto_monto && (
                          <Badge className="bg-green-100 text-green-800 border-green-300">
                            Exacto
                          </Badge>
                        )}
                      </td>
                      <td className="px-2 py-1 text-center">
                        <Button
                          size="sm"
                          variant="default"
                          onClick={() => submitLink(s.folio)}
                          disabled={submittingLink}
                          data-testid={`ncv-link-pick-${s.folio}`}
                        >
                          Linkear
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="border-t pt-3 mt-2">
            <Label className="text-xs">¿No aparece la factura? Ingresa folio manual:</Label>
            <div className="flex gap-2 mt-1">
              <Input
                placeholder="Ej: 2528"
                value={linkSearchTerm}
                onChange={(e) => setLinkSearchTerm(e.target.value)}
                data-testid="ncv-link-folio-manual"
              />
              <Button
                onClick={() => submitLink(linkSearchTerm.trim())}
                disabled={!linkSearchTerm.trim() || submittingLink}
                variant="outline"
              >
                Forzar link
              </Button>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setLinkDlg(null)}>
              Cancelar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Aplicar NC */}
      <Dialog open={!!aplicarDlg} onOpenChange={(o) => !o && setAplicarDlg(null)}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Aplicar NC de venta</DialogTitle>
            <DialogDescription>
              {aplicarDlg && (
                <span>
                  NC <b>{aplicarDlg.nc.folio}</b> · {aplicarDlg.nc.receptor_razon_social}{' '}
                  · <b>{fmtCLP(aplicarDlg.nc.total)}</b>
                  {aplicarDlg.nc.folio_factura_ref && (
                    <span className="block text-xs mt-1">
                      Factura destino: <b>{aplicarDlg.nc.folio_factura_ref}</b>
                    </span>
                  )}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div>
              <Label className="text-sm">
                Motivo / Observación <span className="text-red-500">*</span>
              </Label>
              <Textarea
                value={motivo}
                onChange={(e) => setMotivo(e.target.value)}
                placeholder="Ej: Anulación total por error de facturación — recreación pendiente"
                rows={2}
                data-testid="ncv-aplicar-motivo"
              />
              <p className="text-xs text-gray-500 mt-1">
                Mínimo 5 caracteres (auditoría)
              </p>
            </div>

            {/* Panel dry-run */}
            <div className="bg-blue-50 border border-blue-200 rounded p-3 space-y-2">
              <div className="flex items-center gap-2 text-sm font-semibold text-blue-800">
                <Search className="w-4 h-4" />
                Previsualizar (dry-run)
              </div>
              <p className="text-xs text-blue-700">
                Revisa saldos y qué proforma se anulará antes de confirmar. No modifica
                Alegra ni el ERP.
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={previsualizar}
                disabled={previewBusy || applyBusy}
                data-testid="ncv-preview-btn"
              >
                {previewBusy ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Search className="w-4 h-4 mr-2" />
                )}
                Previsualizar
              </Button>

              {preview && (
                <div
                  className="bg-white border rounded p-2 text-xs space-y-1 mt-2"
                  data-testid="ncv-preview-result"
                >
                  <div className="grid grid-cols-2 gap-1">
                    <span className="text-gray-500">Factura</span>
                    <span className="font-mono text-right">{preview.folio_factura}</span>
                    <span className="text-gray-500">Alegra invoice</span>
                    <span className="font-mono text-right">{preview.alegra_invoice_id}</span>
                    <span className="text-gray-500">Monto NC</span>
                    <span className="font-semibold text-orange-700 text-right">
                      {fmtCLP(preview.total_nc)}
                    </span>
                    <span className="text-gray-500">Saldo factura actual</span>
                    <span className="text-right">
                      {fmtCLP(preview.saldo_factura_actual)}
                    </span>
                    <span className="text-gray-600 font-medium">
                      Saldo factura proyectado
                    </span>
                    <span className="font-bold text-emerald-700 text-right">
                      {fmtCLP(preview.saldo_factura_proyectado)}
                    </span>
                  </div>
                  <div className="border-t pt-1 mt-1 grid grid-cols-2 gap-1">
                    <span className="text-gray-500">Proforma origen</span>
                    <span className="text-right">
                      #{preview.proforma_anulada_num} ({preview.proforma_anulada_estado_actual})
                    </span>
                    <span className="text-gray-500">Proforma nueva (borrador)</span>
                    <span className="text-right font-semibold text-emerald-700">
                      #{preview.proforma_recreada_num_preview}
                    </span>
                  </div>
                  <div className="border-t pt-1 mt-1 space-y-0.5">
                    <div className="flex items-center gap-1">
                      <Badge
                        className={
                          preview.nc_ya_en_alegra
                            ? 'bg-blue-100 text-blue-800 border-blue-300'
                            : 'bg-emerald-100 text-emerald-800 border-emerald-300'
                        }
                      >
                        {preview.accion_alegra}
                      </Badge>
                      <ArrowRight className="w-3 h-3 text-gray-400" />
                      <Badge
                        className={
                          preview.accion_erp?.startsWith('SKIP')
                            ? 'bg-gray-100 text-gray-700 border-gray-300'
                            : 'bg-orange-100 text-orange-800 border-orange-300'
                        }
                      >
                        {preview.accion_erp}
                      </Badge>
                    </div>
                    {preview.nc_ya_en_alegra && !preview.nc_ya_vinculada_a_factura && (
                      <div className="text-red-600 flex items-center gap-1 mt-1">
                        <AlertTriangle className="w-3 h-3" />
                        NC ya existe en Alegra pero NO vinculada a la factura. Requiere
                        curación manual.
                      </div>
                    )}
                    {preview.nc_ya_aplicada_en_erp && (
                      <div className="text-amber-700 flex items-center gap-1 mt-1">
                        <AlertTriangle className="w-3 h-3" />
                        NC ya aplicada en ERP previamente.
                      </div>
                    )}
                  </div>
                </div>
              )}

              <Button
                size="sm"
                onClick={aplicarReal}
                disabled={
                  applyBusy ||
                  motivo.trim().length < 5 ||
                  (preview &&
                    (preview.accion_erp?.startsWith('SKIP') ||
                      (preview.nc_ya_en_alegra && !preview.nc_ya_vinculada_a_factura)))
                }
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="ncv-aplicar-submit"
              >
                {applyBusy ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                )}
                Aplicar NC (crea credit_note y anula proforma)
              </Button>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setAplicarDlg(null)}>
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─────────────── helpers UI ───────────────

function KpiCard({ label, value, tone = 'gray', numeric = true, testid }) {
  const tones = {
    gray: 'bg-gray-50 text-gray-900',
    amber: 'bg-amber-50 text-amber-900 border-amber-200',
    blue: 'bg-blue-50 text-blue-900 border-blue-200',
    indigo: 'bg-indigo-50 text-indigo-900 border-indigo-200',
    green: 'bg-green-50 text-green-900 border-green-200',
    red: 'bg-red-50 text-red-900 border-red-200',
  };
  return (
    <Card className={`${tones[tone]} border`} data-testid={testid}>
      <CardContent className="p-3">
        <div className="text-xs uppercase tracking-wide font-medium opacity-70">
          {label}
        </div>
        <div className={`mt-1 font-bold ${numeric ? 'text-2xl' : 'text-lg'}`}>
          {value}
        </div>
      </CardContent>
    </Card>
  );
}

function CategoryHelp({ cat }) {
  const meta = CATEGORIA_META[cat];
  if (!meta) return null;
  return (
    <div className={`border rounded p-2 mb-3 text-xs ${meta.cls}`}>
      <span className="mr-1">{meta.icon}</span>
      {meta.desc}
    </div>
  );
}

function NCVentaTable({ items, onLink, onQuitarLink, onAplicar }) {
  if (!items.length) {
    return (
      <div className="text-center py-10 text-gray-500 border rounded">
        No hay notas de crédito de venta en esta categoría.
      </div>
    );
  }
  return (
    <div className="border rounded overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-gray-50">
          <tr className="text-left">
            <th className="px-3 py-2">Folio NC</th>
            <th className="px-3 py-2">Fecha</th>
            <th className="px-3 py-2">Cliente</th>
            <th className="px-3 py-2 text-right">Monto</th>
            <th className="px-3 py-2">Factura ref.</th>
            <th className="px-3 py-2">Proforma</th>
            <th className="px-3 py-2">Estado</th>
            <th className="px-3 py-2 text-right">Acciones</th>
          </tr>
        </thead>
        <tbody>
          {items.map((it) => {
            const meta = CATEGORIA_META[it.categoria] || {};
            return (
              <tr
                key={it.folio + it.receptor_rut}
                className="border-t hover:bg-gray-50"
                data-testid={`ncv-row-${it.folio}`}
              >
                <td className="px-3 py-2 font-mono">{it.folio}</td>
                <td className="px-3 py-2 text-gray-600">
                  {fmtDate(it.fecha_emision)}
                </td>
                <td className="px-3 py-2">
                  <div className="font-medium">{it.receptor_razon_social || '—'}</div>
                  <div className="text-xs text-gray-500">{it.receptor_rut}</div>
                </td>
                <td className="px-3 py-2 text-right font-semibold text-orange-700">
                  {fmtCLP(it.total)}
                </td>
                <td className="px-3 py-2">
                  {it.folio_factura_ref ? (
                    <div>
                      <span className="font-mono text-blue-700">
                        {it.folio_factura_ref}
                      </span>
                      {it.factura_original && (
                        <div className="text-xs text-gray-500">
                          {fmtDate(it.factura_original.fecha_emision)} ·{' '}
                          {fmtCLP(it.factura_original.totales?.total || 0)}
                        </div>
                      )}
                    </div>
                  ) : (
                    <span className="text-xs text-gray-400 italic">—</span>
                  )}
                </td>
                <td className="px-3 py-2 text-xs">
                  {it.nc_proforma_anulada_num ? (
                    <div>
                      <span className="text-red-600">
                        #{it.nc_proforma_anulada_num} anulada
                      </span>
                      {it.nc_proforma_recreada_num && (
                        <div className="text-emerald-700">
                          → recreada #{it.nc_proforma_recreada_num}
                        </div>
                      )}
                    </div>
                  ) : (
                    <span className="text-gray-400 italic">—</span>
                  )}
                </td>
                <td className="px-3 py-2">
                  <Badge className={meta.cls || ''}>
                    {meta.icon} {meta.label || it.categoria}
                  </Badge>
                  {it.razon_xml && (
                    <div className="text-xs text-gray-500 mt-1 italic">
                      {it.razon_xml}
                    </div>
                  )}
                  {it.nc_aplicada_motivo && (
                    <div className="text-xs text-gray-600 mt-1">
                      {it.nc_aplicada_motivo}
                    </div>
                  )}
                </td>
                <td className="px-3 py-2 text-right">
                  <div className="flex justify-end gap-1 flex-wrap">
                    {!it.nc_aplicada && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => onLink(it)}
                        data-testid={`ncv-link-btn-${it.folio}`}
                      >
                        <Link2 className="w-3 h-3 mr-1" />
                        Linkear
                      </Button>
                    )}
                    {it.link_origen === 'manual' && !it.nc_aplicada && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => onQuitarLink(it.folio)}
                        data-testid={`ncv-unlink-btn-${it.folio}`}
                      >
                        <Unlink className="w-3 h-3" />
                      </Button>
                    )}
                    {!it.nc_aplicada && it.folio_factura_ref && (
                      <Button
                        size="sm"
                        variant="default"
                        onClick={() => onAplicar(it)}
                        data-testid={`ncv-aplicar-btn-${it.folio}`}
                      >
                        <FileMinus className="w-3 h-3 mr-1" />
                        Aplicar
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
